import React from 'react';
import { toast } from 'react-toastify';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Button, Checkbox, Dialog, FileUploader, Radio, Select, Text } from 'components';
import { Field, Form, Formik } from 'formik';
import { useQuery } from 'hooks/useQuery';
import { emailSendingService, providerService } from 'services';

import { validationSchema } from './validation';

const weekdaysOptions = [
  { value: '1', label: 'Segunda' },
  { value: '2', label: 'Terça' },
  { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' },
  { value: '5', label: 'Sexta' },
];

const parseSignageReportClients = (signageReportClients) => {
  if (!signageReportClients || signageReportClients === 'TODOS') return [0];

  return signageReportClients.split(';').filter(Boolean);
};

export const EditEmailSendingConfigDialog = ({ open, handleClose, config = {}, reloadConfigs }) => {
  if (!open) return null;

  const [signageClients, , loadingSignageClients] = useQuery(
    () => providerService.findClientsExped(),
    [],
  );

  const signageClientsOptions = [{ label: 'TODOS', value: 0 }, ...(signageClients || [])];

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();

      formData.append('client', values.client);
      formData.append('title', values.title);
      formData.append('message', values.message);
      formData.append('recipients', values.recipients);
      formData.append('copy_recipients', values.copy_recipients || '');
      formData.append('weekday', values.weekday);
      formData.append('attachment_type', values.attachment_type);

      if (values.attachment_type === 'signage_report') {
        formData.append(
          'signage_report_clients',
          values.signageReportClients.includes(0) ? 'TODOS' : values.signageReportClients.join(';'),
        );
        formData.append('include_danfe_zip', values.include_danfe_zip);
      } else if (values.attachment) {
        formData.append('attachment', values.attachment);
      } else if (values.removeAttachment) {
        formData.append('remove_attachment', 'true');
      }

      await emailSendingService.updateConfig(config.id, formData);

      toast.success('Configuração atualizada com sucesso!');

      reloadConfigs();
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Erro ao atualizar configuração.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog maxWidth='sm' scroll='paper' onClose={handleClose} open={open}>
      <Dialog.Title onClose={handleClose}>Editar configuração</Dialog.Title>
      <Dialog.Content>
        <Formik
          initialValues={{
            client: config?.client || '',
            recipients: config?.recipients || '',
            copy_recipients: config?.copy_recipients || '',
            title: config?.title || '',
            message: config?.message || '',
            weekday: config?.weekday || '',
            attachment: null,
            removeAttachment: false,
            attachment_type: config?.attachment_type || 'manual',
            signageReportClients: parseSignageReportClients(config?.signage_report_clients),
            include_danfe_zip: config?.include_danfe_zip || false,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {(props) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    variant='outlined'
                    label='Cliente'
                    name='client'
                    size='small'
                    component={Text}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    variant='outlined'
                    label='Destinatário'
                    name='recipients'
                    size='small'
                    component={Text}
                    helperText='Separe múltiplos emails por ponto e vírgula ";"'
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    variant='outlined'
                    label='Cópia'
                    name='copy_recipients'
                    size='small'
                    component={Text}
                    helperText='Separe múltiplos emails por ponto e vírgula ";"'
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    variant='outlined'
                    label='Título'
                    name='title'
                    size='small'
                    component={Text}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    variant='outlined'
                    label='Mensagem'
                    name='message'
                    size='small'
                    multiline
                    rows={5}
                    component={Text}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='subtitle2'>Enviar automaticamente no dia</Typography>
                </Grid>
                {weekdaysOptions.map((weekday) => (
                  <Grid item xs={6} md={2} key={weekday.value}>
                    <Field
                      name='weekday'
                      type='radio'
                      value={weekday.value}
                      label={weekday.label}
                      component={Radio}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Typography variant='subtitle2'>Anexo</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name='attachment_type'
                    type='radio'
                    value='manual'
                    label='Anexo manual'
                    component={Radio}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name='attachment_type'
                    type='radio'
                    value='signage_report'
                    label='Relatório de Sinalização (Expedição)'
                    component={Radio}
                  />
                </Grid>

                {props.values.attachment_type === 'manual' && (
                  <Grid item xs={12}>
                    {config?.attachment_original_name && !props.values.removeAttachment && (
                      <Typography variant='body2' gutterBottom>
                        Anexo atual: {config.attachment_original_name} (escolha um novo arquivo para
                        substituir, ou{' '}
                        <Button
                          size='small'
                          color='secondary'
                          onClick={() => props.setFieldValue('removeAttachment', true)}
                        >
                          remover
                        </Button>
                        )
                      </Typography>
                    )}

                    {props.values.removeAttachment && (
                      <Typography variant='body2' color='secondary' gutterBottom>
                        O anexo atual será removido ao salvar.{' '}
                        <Button
                          size='small'
                          color='primary'
                          onClick={() => props.setFieldValue('removeAttachment', false)}
                        >
                          desfazer
                        </Button>
                      </Typography>
                    )}

                    {!config?.attachment_original_name && (
                      <Typography variant='body2' gutterBottom>
                        Nenhum anexo enviado ainda
                      </Typography>
                    )}

                    {!props.values.removeAttachment && (
                      <Field name='attachment' component={FileUploader} />
                    )}
                  </Grid>
                )}

                {props.values.attachment_type === 'signage_report' && (
                  <Grid item xs={12}>
                    <Field
                      name='signageReportClients'
                      label='Clientes vinculados'
                      component={Select}
                      variant='outlined'
                      fullWidth
                      size='small'
                      multiple
                      loading={loadingSignageClients}
                      options={signageClientsOptions.map((client) => ({
                        ...client,
                        disabled:
                          props.values.signageReportClients.includes(0) && client.value !== 0,
                      }))}
                    />
                  </Grid>
                )}

                {props.values.attachment_type === 'signage_report' && (
                  <Grid item xs={12}>
                    <Field
                      name='include_danfe_zip'
                      label='Anexar DANFEs das notas em ZIP'
                      component={Checkbox}
                    />
                  </Grid>
                )}

                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={handleClose} color='primary'>
                    Fechar
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    loading={props.isSubmitting}
                    onClick={props.handleSubmit}
                    style={{ marginLeft: '8px' }}
                  >
                    Salvar
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Dialog.Content>
    </Dialog>
  );
};
